import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { registrarListener, type ConfirmOptions, type ToastItem } from '../../lib/toast';
import { Modal } from './Modal';
import { Button } from './Button';

/**
 * Pinta los toasts y la confirmación modal emitidos desde lib/toast.ts.
 * Un único provider en main.tsx. Los toasts se apilan abajo a la derecha,
 * se cierran solos (o con la X) y no roban el foco; la confirmación sí es
 * modal y devuelve el foco al cerrarse.
 */

const ESTILO: Record<ToastItem['kind'], { box: string; icon: string; label: string }> = {
  success: { box: 'border-ok-300 bg-ok-50 text-ok-800', icon: '✓', label: 'Correcto' },
  error: { box: 'border-bad-300 bg-bad-50 text-bad-800', icon: '!', label: 'Error' },
  warning: { box: 'border-warn-300 bg-warn-50 text-warn-800', icon: '!', label: 'Atención' },
  info: { box: 'border-accent-300 bg-accent-50 text-accent-800', icon: 'i', label: 'Información' },
};

type Confirmacion = { opts: ConfirmOptions; resolve: (ok: boolean) => void };

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [confirmacion, setConfirmacion] = useState<Confirmacion | null>(null);
  const timers = useRef(new Map<number, number>());

  const cerrar = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const h = timers.current.get(id);
    if (h) { window.clearTimeout(h); timers.current.delete(id); }
  }, []);

  useEffect(() => {
    registrarListener({
      onToast: (t) => {
        setToasts((prev) => [...prev.slice(-4), t]);
        timers.current.set(t.id, window.setTimeout(() => cerrar(t.id), t.duration));
      },
      onConfirm: (opts, resolve) => setConfirmacion({ opts, resolve }),
    });
    const t = timers.current;
    return () => { registrarListener(null); t.forEach((h) => window.clearTimeout(h)); };
  }, [cerrar]);

  const resolver = (ok: boolean) => {
    confirmacion?.resolve(ok);
    setConfirmacion(null);
  };

  return (
    <>
      {children}
      <div aria-live="polite" aria-relevant="additions" className="pointer-events-none fixed inset-x-0 bottom-4 z-[60] flex flex-col items-end gap-2 px-4 sm:inset-x-auto sm:right-4">
        {toasts.map((t) => {
          const e = ESTILO[t.kind];
          return (
            <div
              key={t.id}
              role={t.kind === 'error' ? 'alert' : 'status'}
              className={`pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-lg border px-3.5 py-2.5 text-sm shadow-lg ${e.box}`}
            >
              <span aria-label={e.label} className="mt-0.5 inline-grid h-5 w-5 shrink-0 place-items-center rounded-full bg-white/70 text-xs font-bold">{e.icon}</span>
              <div className="min-w-0 flex-1">
                <div className="font-semibold">{t.title}</div>
                {t.detail && <div className="mt-0.5 whitespace-pre-line text-xs opacity-90">{t.detail}</div>}
              </div>
              <button type="button" onClick={() => cerrar(t.id)} aria-label="Cerrar aviso" className="cursor-pointer rounded p-0.5 opacity-70 hover:opacity-100">✕</button>
            </div>
          );
        })}
      </div>

      {confirmacion && (
        <Modal
          open
          onClose={() => resolver(false)}
          title={confirmacion.opts.title}
          size="sm"
          footer={<>
            <Button variant="secondary" onClick={() => resolver(false)}>{confirmacion.opts.cancelLabel ?? 'Cancelar'}</Button>
            <Button variant={confirmacion.opts.danger ? 'danger' : 'primary'} autoFocus onClick={() => resolver(true)}>{confirmacion.opts.confirmLabel ?? 'Confirmar'}</Button>
          </>}
        >
          {confirmacion.opts.message && <p className="whitespace-pre-line text-sm text-slate-700">{confirmacion.opts.message}</p>}
        </Modal>
      )}
    </>
  );
}
